/**
 * Central session state for the portal.
 *
 * The session itself lives in an HttpOnly cookie that JavaScript cannot read,
 * so "am I signed in?" is answered by calling `/api/me` and caching the result.
 * A 401 is the normal signed-out answer, not an error worth surfacing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LoginInput, Me } from '@mindpeer/auth-shared';
import { ApiError, api, type LoginResult } from '@/lib/api';

export interface AuthState {
  /** Undefined until the first `/api/me` settles; null when signed out. */
  user: Me | null;
  /** True during the initial session probe, before routes may decide. */
  loading: boolean;
  login: (input: LoginInput) => Promise<LoginResult>;
  logout: (global?: boolean) => Promise<void>;
  /** Re-read the session after something changes it server-side. */
  refresh: () => Promise<Me | null>;
  /** Replace the cached user without a round trip. */
  setUser: (user: Me | null) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<Me | null> => {
    try {
      const { user: me } = await api.me();
      setUser(me);
      return me;
    } catch (error) {
      // Only a 401 means "signed out". Anything else (network, 5xx) must not be
      // mistaken for it, or a blip would silently bounce the user to sign-in.
      if (error instanceof ApiError && error.isUnauthenticated) {
        setUser(null);
        return null;
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await refresh();
      } catch {
        // Leave `user` as null; the sign-in page will report failures itself.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(async (input: LoginInput): Promise<LoginResult> => {
    const result = await api.login(input);
    setUser(result.user);
    return result;
  }, []);

  const logout = useCallback(async (global = false): Promise<void> => {
    try {
      await api.logout(global);
    } finally {
      // Even if the call failed, this browser should stop acting signed in.
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, refresh, setUser }),
    [user, loading, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
